import {
  useEffect,
  useCallback,
  useRef,
  useState,
  cloneElement,
  createElement,
  useMemo,
} from "react";
import { Modal } from "antd";
import { useTranslation } from "next-i18next";
import { useDeepCompareEffect } from "react-use";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import { projectActions } from "@/redux/stores/projectStore";
import { getDashBoardState, getProjectState } from "@/redux/store";
import {
  useCheckAvatarPermissionMutation,
  useLazyGetMyAvatarsQuery,
  useLazyGetVoicesQuery,
} from "@/redux/api/myavatarApi";
import { VoiceCloneProvider } from "@/constants/constants";
import type { ProjectType, Scene } from "@/types/type";
import type { MyAvatarAsset, NotSharedAvatar } from "@/types/models";
type MethodReturnType = {
  is: boolean;
  target: string[];
};

type MethodReturnSharePermissionType = {
  is: boolean;
  target: NotSharedAvatar[];
}

// 프로젝트 내의 전처리 후 마이아바타 필드 교체
export const setAfterPreprocessMyAvatars = (args: {
  avatars: MyAvatarAsset[];
  project: ProjectType;
}) => {
  const { avatars, project } = args;

  const myAvatarMap = avatars
    .filter((avatar) => !avatar.preprocess)
    .reduce<{
      [avatarId: string]: MyAvatarAsset;
    }>((acc, avatar) => {
      return {
        ...acc,
        [avatar.id]: avatar,
      };
    }, {});

  return {
    ...project,
    scenes: project.scenes.map((scene) => {
      return {
        ...scene,
        clips: scene.clips.map((clip) => {
          if (
            clip.type === "aiModel" &&
            (
              clip.avatarType === "dreamAvatar" ||
              clip.avatarType === "customAvatar"
              // clip.avatarType === "aiGeneratedAvatar" ||
              // clip.avatarType === "aiGeneratedPhotoAvatar"
            ) &&
            clip.model?.ai_name
          ) {
            if (Object.keys(myAvatarMap).includes(clip.model.ai_name)) {
              clip.model.preprocess_path =
                myAvatarMap[clip.model.ai_name].fileUrl;
            } else {
              delete clip.model.preprocess_path;
            }
          }
          return clip;
        }),
      };
    }),
  };
};

// 프로젝트 내의 전처리 전 마이아바타 필드 내용
export const getBeforePreprocessMyAvatarsParams = (args: {
  avatars: MyAvatarAsset[];
  project: ProjectType;
}) => {
  const { avatars, project } = args;

  const myAvatarParams: { [key: string]: any } = avatars
    .filter((avatar) => avatar.preprocess)
    .reduce((acc, avatar) => {
      return {
        ...acc,
        [avatar.id]: {
          ...avatar.preprocess,
          sceneIndex: [],
        },
      };
    }, {});

  const myAvatarMap = project.scenes.reduce(
    (acc: { [key: string]: any }, scene, index) => {
      scene.clips.forEach((clip) => {
        if (
          clip.type === "aiModel" &&
          (
            clip.avatarType === "dreamAvatar" ||
            clip.avatarType === "customAvatar" ||
            clip.avatarType === "aiGeneratedAvatar" ||
            clip.avatarType === "aiGeneratedPhotoAvatar"
          ) &&
          clip.model?.ai_name &&
          Object.keys(myAvatarParams).includes(clip.model.ai_name)
        ) {
          const key = clip.model.ai_name;

          if (!Object.keys(acc).includes(key)) {
            acc[key] = {
              ...myAvatarParams[key],
              sceneIndex: [index],
            };
          } else {
            acc[key] = {
              ...acc[key],
              sceneIndex: acc[key].sceneIndex.includes(index)
                ? acc[key].sceneIndex
                : [...acc[key].sceneIndex, index],
            };
          }
        }
      });
      return acc;
    },
    {}
  );

  return Object.values(myAvatarMap).reduce(
    (
      acc: { dreamAvatar: any[]; customAvatar: any[] },
      item: { [key: string]: any }
    ) => {
      if (item.avatarType !== "customAvatar") {
        acc.dreamAvatar = [...acc.dreamAvatar, item];
      } else {
        acc.customAvatar = [...acc.customAvatar, item];
      }
      return acc;
    },
    {
      dreamAvatar: [],
      customAvatar: [],
    }
  );
};

// 배경 조정 속성 주입
export const setMyAvatarsMaskingOptions = (args: {
  avatars: MyAvatarAsset[];
  project: ProjectType;
}) => {
  const { avatars, project } = args;

  const myAvatarMap = avatars
    .filter((avatar) => "threshold" in avatar && "kernelSize" in avatar)
    .reduce<{
      [avatarId: string]: MyAvatarAsset;
    }>((acc, avatar) => {
      return {
        ...acc,
        [avatar.id]: avatar,
      };
    }, {});

  return {
    ...project,
    scenes: project.scenes.map((scene) => {
      return {
        ...scene,
        clips: scene.clips.map((clip) => {
          if (
            clip.type === "aiModel" &&
            (
              clip.avatarType === "dreamAvatar" ||
              clip.avatarType === "customAvatar"
            ) &&
            clip.model?.ai_name
          ) {
            if (Object.keys(myAvatarMap).includes(clip.model.ai_name)) {
              clip.model.masking = {
                ...clip.model.masking,
                threshold: myAvatarMap[clip.model.ai_name].threshold,
                kernel_size: myAvatarMap[clip.model.ai_name].kernelSize,
                // TODO: fine tuning 기능이 추가되면 추가 프로퍼티가 필요할 수도 있음
              };
            }
          }
          return clip;
        }),
      };
    }),
  };
};

export function useCheckMyAvatarStatus() {
  const project = useAppSelector((store) => getProjectState(store).project);
  const { workspaceList, selectedWorkspaceIndex } = useAppSelector((store) => getDashBoardState(store) ?? false);

  const [myAvatarsRefetch, myAvatarsResult] = useLazyGetMyAvatarsQuery();
  const [projectAvatars, setProjectAvatars] = useState<string[]>([]);

  const workspaceId = useMemo(() => {
    return workspaceList[selectedWorkspaceIndex]?.workspaceId || project?.workspaceId && project?.workspaceId[0] || '';
  }, [project?.workspaceId, selectedWorkspaceIndex, workspaceList]);

  /**
   * `getSharePermissionAvatar` 함수가 계속해서 호출되는 현상이 있음
   * 
   * 이유:
   *   1. project.scenes가 일부 변경됨
   *   2. `clipCustomAvatars` 배열이 새로 만들어짐 (값은 같으나 새로운 배열 주소를 가짐)
   *   3. `getSharePermissionAvatar` 함수가 새로 만들어짐
   *   4. `getSharePermissionAvatar` 함수가 호출됨
   * 
   * 해결:
   *   1. `clipCustomAvatars`를 `memoizedClipCustomAvatars`로 변경
   *   2. `useDeepCompareEffect`를 사용하여 `memoizedClipCustomAvatars`의 값이\
   *      실제로 변경될 때만 `setClipCustomAvatars`를 호출하여\
   *      `clipCustomAvatars`를 변경하도록 함
   *   3. `clipCustomAvatars`가 원하는 때에만 새로운 값으로 변경되므로\
   *      계속해서 호출되는 현상이 해결됨
   * 
   * 여전히 남아있는 문제:
   *   - `memoizedClipCustomAvatars`가 계속해서 새롭게 만들어지는 문제는 해결되지 않음
   */
  /**
   * 씬에서 쓰인 customAvatar 목록만 가져온다
   * -> 공유권한 체크를 위해서임
   */
  const memoizedClipCustomAvatars = useMemo(() => {
    if(!project?.scenes) return [];
    return project.scenes.flatMap(scene =>
      scene?.clips
      ?.filter(clip => clip?.type === 'aiModel' && clip?.avatarType === 'customAvatar')
      ?.map(clip => clip?.model?.ai_name)
    ) 
  }, [project?.scenes])

  const [clipCustomAvatars, setClipCustomAvatars] = useState<string[]>(
    memoizedClipCustomAvatars,
  )

  useDeepCompareEffect(() => {
    setClipCustomAvatars(memoizedClipCustomAvatars)
  }, [memoizedClipCustomAvatars])

  useEffect(() => {
    const avatars =
      project?.scenes.reduce((acc: string[], scene) => {
        return [
          ...acc,
          ...scene.clips
            .filter(
              (clip) =>
                clip.type === "aiModel" &&
                (
                  clip.avatarType === "dreamAvatar" ||
                  clip.avatarType === "customAvatar"
                ) &&
                clip.model?.ai_name &&
                !acc.includes(clip.model.ai_name)
            )
            .map((clip) => clip.model.ai_name),
        ];
      }, []) || [];

    setProjectAvatars((prev) => {
      if (
        prev.length !== avatars.length ||
        prev.some((item) => !avatars.includes(item)) ||
        avatars.some((item) => !prev.includes(item))
      ) {
        return avatars;
      }
      return prev;
    });
  }, [project?.scenes]);

  // 프로젝트 내의 에러가 발생한 마이아바타 조회
  const getOccurredErrorMyAvatars: () => Promise<MethodReturnType> = useCallback(async () => {
    const myAvatars = await myAvatarsRefetch({ workspaceId }).unwrap();
    const myAvatarIds = myAvatars
      .filter((avatar) => avatar.error)
      .map((avatar) => avatar.id);

    const list = myAvatarIds.filter((item) => (
      projectAvatars.includes(item)
    ));

    if (list.length > 0) {
      return {
        is: false,
        target: [],
      };
    }
    return {
      is: false,
      target: [],
    };
  }, [myAvatarsRefetch, projectAvatars, workspaceId]);

  const occurredErrorMyAvatars = useRef<MethodReturnType>({
    is: true,
    target: [],
  });

  useEffect(() => {
    getOccurredErrorMyAvatars().then((value) => {
      occurredErrorMyAvatars.current = value;
    });
  }, [getOccurredErrorMyAvatars]);

  // 프로젝트 내의 삭제된 마이아바타 조회
  const getDeletedMyAvatars: () => Promise<MethodReturnType> = useCallback(async () => {
    const myAvatars = await myAvatarsRefetch({ workspaceId }).unwrap();

    const myAvatarIds = myAvatars
      .map((avatar) => avatar.id);

    const list = projectAvatars
      .filter((avatar) => !myAvatarIds.includes(avatar))

    if (list.length > 0) {
      return {
        is: false,
        target: [],
      };
    }
    return {
      is: false,
      target: [],
    };
  }, [myAvatarsRefetch, projectAvatars, workspaceId]);

  const deletedMyAvatars = useRef<MethodReturnType>({ is: true, target: [] });

  useEffect(() => {
    getDeletedMyAvatars().then((value) => {
      deletedMyAvatars.current = value;
    });
  }, [getDeletedMyAvatars]);

  const [myVoicesRefetch, myVoicesResult] = useLazyGetVoicesQuery();
  const [projectVoices, setProjectVoices] = useState<string[]>([]);

  useEffect(() => {
    const voices =
      project?.scenes.reduce((acc: string[], scene) => {
        return [
          ...acc,
          ...scene.scripts
            .map((script) => script.tts?.id)
            .filter((id: string) => id && !acc.includes(id)),
        ];
      }, []) || [];

    setProjectVoices((prev) => {
      if (
        prev.length !== voices.length ||
        prev.some((item) => !voices.includes(item)) ||
        voices.some((item) => !prev.includes(item))
      ) {
        return voices;
      }
      return prev;
    });
  }, [project?.scenes]);

  // 프로젝트 내의 삭제된 보이스클론 조회
  const getDeletedMyVoices: () => Promise<MethodReturnType> = useCallback(async () => {
    const myVoices = await myVoicesRefetch({ workspaceId }).unwrap();
    const myVoiceIds = myVoices
      .map((voice) => voice.id);

    const list = projectVoices
      .filter((voice) => (
        VoiceCloneProvider.some((provider) => voice.startsWith(provider)) &&
        !myVoiceIds.includes(voice)
      ));

    if (list.length > 0) {
      return {
        is: false,
        target: [],
      };
    }
    return {
      is: false,
      target: [],
    };
  }, [myVoicesRefetch, projectVoices, workspaceId]);

  const deletedMyVoices = useRef<MethodReturnType>({ is: true, target: [] });

  useEffect(() => {
    getDeletedMyVoices().then((value) => {
      deletedMyVoices.current = value;
    });
  }, [getDeletedMyVoices]);

  /**
   * custom avatar 공유되었는지 권한 체크
   * -> sample이랑 자기가 만든건 제외시키고 권한 체크
   * -> 지금은 customAvatar 기준이므로 dreamAvatar는 전부 걸러낸다
   */
  const [checkAvatarPermission] = useCheckAvatarPermissionMutation();
  const getSharePermissionAvatar: () => Promise<MethodReturnSharePermissionType> = useCallback(async() => {
    /**
     * 실제 프로젝트에서 쓰인 customAvatar/dreamAvatar id 값 뽑아낸다.
     */
    const myAvatars = await myAvatarsRefetch({ workspaceId }).unwrap();
    const avatarList: string[] | [] = [
    ...new Set(
      myAvatars
        .filter((avatar: MyAvatarAsset) => avatar.type === 'customAvatar' && !avatar?.isSample && !avatar?.isOwn)
        .map((avatar: MyAvatarAsset) => avatar.id)
        .concat(clipCustomAvatars.filter(avatar => !myAvatars.some(val => val.type === 'customAvatar' && val.id === avatar)))
      )
    ];
    const result = await checkAvatarPermission({ avatarList }).unwrap();

    if(result.length > 0) {
      return {
        is: true,
        target: result
      }
    } 
    return {
      is: false,
      target: []
    }
  }, [checkAvatarPermission, clipCustomAvatars, myAvatarsRefetch, workspaceId])

  const notSharedAvatars = useRef<MethodReturnSharePermissionType>({ is: true, target: [] });

  useEffect(() => {
    getSharePermissionAvatar().then((value) => {
      notSharedAvatars.current = value;
    })
  }, [getSharePermissionAvatar])

  const [myAssetErrorModalMethod, myAssetErrorModalContext ] = Modal.useModal();
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();

  const selectedLeftTab = useAppSelector(
    (store) => getProjectState(store).selectedLeftTab
  );

  const myAssetErrorModalMessages = {
    errorAvatar: t("Please change the unusable avatar of the project."),
    deletedAvatar: t("Please change the deleted avatar of the project."),
    deletedVoice: t("Please change the deleted voice of the project."),
    notSharedAvatar: t("The scene contains an avatar that you do not have permission to share. Please change your model and try again."),
  }
  const myAssetErrorModalConfig = {
    title: t("Export error"),
    footer: (originNode: any) => {
      return cloneElement(originNode, {
        ...originNode.props,
        children: [originNode.props.children[1]],
      });
    },
  };

  const myAvatarErrorModal = () =>
    myAssetErrorModalMethod.error({
      ...myAssetErrorModalConfig,
      content: createElement("p", {}, myAssetErrorModalMessages.errorAvatar),
      onOk: () => {
        if (occurredErrorMyAvatars.current.is && project) {
          const target = project.scenes.reduce(
            (
              acc: { sceneIndex: number; clipId: string } | null,
              scene: Scene,
              index: number
            ) => {
              if (acc === null) {
                const targetClip = scene.clips.find(
                  (clip) =>
                    clip.model?.ai_name ===
                    occurredErrorMyAvatars.current.target[0]
                );
                if (targetClip) {
                  return { sceneIndex: index, clipId: targetClip.id };
                }
              }
              return acc;
            },
            null
          );

          if (target) {
            dispatch(projectActions.selectObject(target));
            if (selectedLeftTab !== "aiModel") {
              dispatch(projectActions.selectLeftSiderTab("aiModel"));
            }
          }
        }
      },
    });

  const myAvatarDeletedModal = () =>
    myAssetErrorModalMethod.error({
      ...myAssetErrorModalConfig,
      content: createElement("p", {}, myAssetErrorModalMessages.deletedAvatar),
      onOk: () => {
        if (deletedMyAvatars.current.is && project) {
          const target = project.scenes.reduce(
            (
              acc: { sceneIndex: number; clipId: string } | null,
              scene: Scene,
              index: number
            ) => {
              if (acc === null) {
                const targetClip = scene.clips.find(
                  (clip) =>
                    clip.model?.ai_name === deletedMyAvatars.current.target[0]
                );
                if (targetClip) {
                  return { sceneIndex: index, clipId: targetClip.id };
                }
              }
              return acc;
            },
            null
          );

          if (target) {
            dispatch(projectActions.selectObject(target));
            if (selectedLeftTab !== "aiModel") {
              dispatch(projectActions.selectLeftSiderTab("aiModel"));
            }
          }
        }
      },
    });

  const myVoiceDeletedModal = () =>
    myAssetErrorModalMethod.error({
      ...myAssetErrorModalConfig,
      content: createElement("p", {}, myAssetErrorModalMessages.deletedVoice),
      onOk: () => {
        if (deletedMyVoices.current.is && project) {
          const target = project.scenes.reduce(
            (
              acc: { sceneIndex: number; scriptIndex: number } | null,
              scene: Scene,
              index: number
            ) => {
              if (acc === null) {
                const targetScriptIndex = scene.scripts.findIndex(
                  (script) =>
                    script?.tts?.id === deletedMyVoices.current.target[0]
                );
                if (targetScriptIndex !== -1) {
                  return { sceneIndex: index, scriptIndex: targetScriptIndex };
                }
              }
              return acc;
            },
            null
          );

          if (target) {
            dispatch(projectActions.selectScene(target.sceneIndex));
            // 보이스 모달 열기 리덕스에 없음
          }
        }
      },
    });

  const notSharedAvatarModal = () => myAssetErrorModalMethod.error({
    ...myAssetErrorModalConfig,
    content: createElement("p", {}, myAssetErrorModalMessages.notSharedAvatar),
    onOk: () => {
      if(notSharedAvatars.current.is && project) {
        const findNotSharedAvatarSceneIndex = project.scenes.findIndex(scene =>
          scene.clips.some(clip =>
            clip.type === 'aiModel' &&
            clip?.avatarType === 'customAvatar' &&
            clip?.model?.ai_name &&
            notSharedAvatars.current.target.some(target => target.id === clip.model!.ai_name)
          )
        )

        if(findNotSharedAvatarSceneIndex !== 1) {
          dispatch(projectActions.selectScene(findNotSharedAvatarSceneIndex));
        }
      }
    }
  })

  return {
    myAvatars: myAvatarsResult,
    myAvatarsRefetch,
    getOccurredErrorMyAvatars,
    occurredErrorMyAvatars,
    getDeletedMyAvatars,
    deletedMyAvatars,
    myVoices: myVoicesResult,
    getDeletedMyVoices,
    deletedMyVoices,
    myAssetErrorModalContext,
    myAvatarErrorModal,
    myAvatarDeletedModal,
    myVoiceDeletedModal,
    notSharedAvatarModal,
    getSharePermissionAvatar,
  };
}
